import { useState, ChangeEvent, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, ArrowLeftRight, CheckCircle2, AlertCircle } from "lucide-react";
import YAML from "yaml";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_YAML = `siteName: 247 DevTools Hub
seoOptimized: true
developerProductivity:
  category: Offline Utility Suite
  privacyGuaranteed: true
  activeUsers: 12500
  uptime: 99.99%
  supportedLanguages:
    - JSON
    - XML
    - YAML
    - CSV
    - SQL
    - HTML
    - CSS
    - JS
localRAMStorage:
  engine: In-Browser Client-Side Node
  cookiesEnabled: false
  encryptionLevel: AES-256-Local
recentFeatures:
  - id: excel-grid-01
    name: Advanced Formula Editor
    formulas:
      - SUM
      - AVERAGE
      - IF
      - UPPER
  - id: bcrypt-02
    name: Secure Password Hashing
    rounds: 10`;

export function YamlFormatter() {
  const [input, setInput] = useState(SAMPLE_YAML);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"yaml-to-json" | "json-to-yaml">("yaml-to-json");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const processText = (text: string, currentMode: "yaml-to-json" | "json-to-yaml") => {
    if (!text.trim()) {
      setOutput("");
      setError(null);
      return;
    }
    try {
      if (currentMode === "yaml-to-json") {
        const parsed = YAML.parse(text);
        if (parsed === undefined) throw new Error("Empty or invalid YAML");
        setOutput(JSON.stringify(parsed, null, 2));
      } else {
        const parsed = JSON.parse(text);
        setOutput(YAML.stringify(parsed));
      }
      setError(null);
    } catch (e: any) {
      setError(e.message || "Invalid input");
      setOutput("");
    }
  };

  useEffect(() => {
    processText(SAMPLE_YAML, "yaml-to-json");
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    processText(val, mode);
  };

  const toggleMode = () => {
    const newMode = mode === "yaml-to-json" ? "json-to-yaml" : "yaml-to-json";
    setMode(newMode);
    setInput(output);
    processText(output, newMode);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="YAML to JSON Converter"
      description="Convert between YAML and JSON formats instantly. Validate your data and beautify your configuration files with our free tool."
      h1="YAML to JSON Converter"
      category="JSON Tools"
      categoryPath="/json-tools"
      howToUse={[
        "Paste your YAML or JSON code in the input area.",
        "Use the switch button to choose between 'YAML to JSON' or 'JSON to YAML'.",
        "View the converted and validated output instantly."
      ]}
      faq={[
        { question: "How to convert YAML to JSON?", answer: "Simply paste your YAML content and our tool automatically converts it to formatted JSON." },
        { question: "Is my YAML valid?", answer: "Our tool validates your YAML input and displays errors if the structure is invalid." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button onClick={toggleMode} variant="outline" className="gap-2 w-full md:w-auto">
            <ArrowLeftRight className="h-4 w-4" />
            Switch to {mode === "yaml-to-json" ? "JSON to YAML" : "YAML to JSON"}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {mode === "yaml-to-json" ? "YAML Input" : "JSON Input"}
            </label>
            <Textarea
              placeholder={mode === "yaml-to-json" ? "key: value" : '{"key": "value"}'}
              className="min-h-[400px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">
                {mode === "yaml-to-json" ? "JSON Output" : "YAML Output"}
              </label>
              {error ? (
                <span className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Error</span>
              ) : output ? (
                <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Valid</span>
              ) : null}
            </div>
            <Textarea
              className="min-h-[400px] bg-slate-50"
              value={error ? error : output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!!error || !output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setError(null); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
